import {ModernizationDegreeApiInterface} from "../api/interface/modernization-degree";
import {QualityFurnishingsApiInterface} from "../api/interface/quality-furnishings";
import type {ModernizationDegreeResponse} from "../api/payload-types/modernization-degree";
import type {QualityFurnishingsResponse} from "../api/payload-types/quality-furnishings";
import {generateDefaultQueryError} from "../util/errorUtil";
import {selectCredentials} from "./authSlice";
import {
    type QualityFurnishingsOutputs,
    setModernizationDegreeError,
    setQualityFurnishingsError,
} from "./objectInformationSlice";
import {selectAllInspectionDerivedParams, selectAllInspectionParams, selectSegment} from "./objectParametersSlice";
import type {RevaThunkAction} from "./revaHooks";

function toQualityFurnishingsOutputs(response: QualityFurnishingsResponse | undefined): QualityFurnishingsOutputs {
    return {
        nhk_crafts_matrix: response?.nhk_crafts_matrix ?? null,
        nhk_crafts_based_furnishings: response?.nhk_crafts_based_furnishings ?? null,
    };
}

function collectParamValues(keys: string[], state: unknown): Record<string, unknown> {
    const derived = selectAllInspectionDerivedParams(state as never) as Record<string, unknown>;
    const params = selectAllInspectionParams(state as never) as Record<string, unknown>;
    return Object.fromEntries(keys.map((k) => [k, derived[k] ?? params[k]]).filter(([, v]) => v != null)) as Record<
        string,
        unknown
    >;
}

/**
 * Hält die zuletzt gestartete Abfrage eines Endpunkts. Ihre Antwort speichert der Aufrufer, daher darf eine
 * spät eintreffende ältere kein neueres Ergebnis überschreiben: {@link start} bricht die vorige ab.
 */
function latestRequest() {
    let controller: AbortController | null = null;
    return {
        start(): AbortSignal {
            controller?.abort();
            controller = new AbortController();
            return controller.signal;
        },
        abort(): void {
            controller?.abort();
            controller = null;
        },
    };
}

const qualityFurnishingsRequest = latestRequest();
const modernizationDegreeRequest = latestRequest();

/**
 * Bricht die laufende qualityFurnishings-Abfrage ab, ohne eine neue zu starten — für Stellen, an denen
 * ihr Ergebnis nicht mehr gilt (Reset, ungültige Zeile).
 */
export const abortQualityFurnishingsQuery = qualityFurnishingsRequest.abort;

/** Bricht die laufende modernizationDegree-Abfrage ab, ohne eine neue zu starten (Reset). */
export const abortModernizationDegreeQuery = modernizationDegreeRequest.abort;

/**
 * Async thunk to run qualityFurnishings request. Fehlt ein Feld in der Antwort oder schlägt der Aufruf fehl,
 * ist es null (#19376: fail hard — analog zu fetchModernizationDegree). Eine neue Abfrage bricht die vorige ab.
 * @param crafts list of spec param keys (e.g. 'prop_craft_exterior_walls_weights')
 * @return die Ergebnisse, oder null, wenn die Abfrage abgebrochen wurde und ihr Ergebnis verworfen werden muss
 */
export const queryQualityFurnishingsAsync =
    (crafts: string[]): RevaThunkAction<Promise<QualityFurnishingsOutputs | null>> =>
    async (dispatch, getState, {serviceApi}) => {
        const signal = qualityFurnishingsRequest.start();
        const state = getState();
        const segment = selectSegment(state as never);
        const credentials = selectCredentials(state as never);
        if (!segment || !credentials) {
            return toQualityFurnishingsOutputs(undefined);
        }

        const apiInterface = new QualityFurnishingsApiInterface(segment, collectParamValues(crafts, state), signal);
        dispatch(setQualityFurnishingsError(null));
        try {
            const response = await apiInterface.execute(serviceApi, credentials);
            return signal.aborted ? null : toQualityFurnishingsOutputs(response);
        } catch (err) {
            if (signal.aborted) {
                return null;
            }
            dispatch(setQualityFurnishingsError(generateDefaultQueryError(err as Error)));
            return toQualityFurnishingsOutputs(undefined);
        }
    };

/**
 * Async thunk to run modernizationDegree request. Schlägt der Aufruf fehl, ist das Ergebnis leer
 * (#19376: fail hard). Eine neue Abfrage bricht die vorige ab.
 * @param inputParams spec params object for MODERNIZATION_DEGREE, keys are the param names
 * @return das Ergebnis, oder null, wenn die Abfrage abgebrochen wurde und ihr Ergebnis verworfen werden muss
 */
export const queryModernizationDegreeAsync =
    (inputParams: Record<string, unknown>): RevaThunkAction<Promise<ModernizationDegreeResponse | null>> =>
    async (dispatch, getState, {serviceApi}) => {
        const signal = modernizationDegreeRequest.start();
        const state = getState();
        const segment = selectSegment(state as never);
        const credentials = selectCredentials(state as never);
        if (!segment || !credentials) {
            return {};
        }

        const apiInterface = new ModernizationDegreeApiInterface(
            segment,
            collectParamValues(Object.keys(inputParams), state),
            signal,
        );
        dispatch(setModernizationDegreeError(null));
        try {
            const response = await apiInterface.execute(serviceApi, credentials);
            return signal.aborted ? null : response;
        } catch (err) {
            if (signal.aborted) {
                return null;
            }
            dispatch(setModernizationDegreeError(generateDefaultQueryError(err as Error)));
            return {};
        }
    };
